import { normalizeStatsDateRangeQuery } from '@services/statsQueryUtils'
import { serviceOptions } from '@services/api'

export interface GetPatientStatsQuery {
  siteId?: number
  departmentCode?: string
  zipCode?: string
  startDate: string
  endDate: string
}

export interface StatsPatientCountDTO {
  total: number
}

export interface StatsPatientOriginByDepartmentDTO {
  departmentCode: string
  total: number
}

export interface StatsPatientOriginByMunicipalityDTO {
  zipCode: string
  city: string
  total: number
}

export interface StatsPatientCareTypeBreakdownDTO {
  careType: string
  total: number
  percentage: number
}

export interface StatsPatientGenderBreakdownDTO {
  gender: string
  total: number
  percentage: number
}

export interface StatsPatientAgeSummaryDTO {
  meanAge: number
  medianAge: number
}

const getBasePatientStatsQuery = (params: GetPatientStatsQuery) => ({
  siteId: params.siteId,
  startDate: params.startDate,
  endDate: params.endDate,
})

const getPatientGenderStatsQuery = (params: GetPatientStatsQuery) => ({
  siteId: params.siteId,
  departmentCode: params.departmentCode,
  zipCode: params.zipCode,
})

const getPatientAgeSummaryQuery = (params: GetPatientStatsQuery) => ({
  siteId: params.siteId,
  departmentCode: params.departmentCode,
  zipCode: params.zipCode,
})

const getAxiosClient = () => {
  if (!serviceOptions.axios) {
    throw new Error('Axios client is not configured')
  }

  return serviceOptions.axios
}

export const fetchPatientStatsCount = async (
  params: GetPatientStatsQuery,
): Promise<StatsPatientCountDTO> => {
  const normalizedParams = normalizeStatsDateRangeQuery(params)

  const response = await getAxiosClient().get<StatsPatientCountDTO>(
    '/stats/patients/count',
    { params: normalizedParams },
  )

  return response.data
}

export const fetchPatientOriginByDepartments = async (
  params: GetPatientStatsQuery,
): Promise<StatsPatientOriginByDepartmentDTO[]> => {
  const normalizedParams = normalizeStatsDateRangeQuery(params)

  const response = await getAxiosClient().get<StatsPatientOriginByDepartmentDTO[]>(
    '/stats/patients/origin/departments',
    { params: normalizedParams },
  )

  return response.data
}

export const fetchPatientOriginByMunicipalities = async (
  params: GetPatientStatsQuery,
): Promise<StatsPatientOriginByMunicipalityDTO[]> => {
  const normalizedParams = normalizeStatsDateRangeQuery(params)

  const response = await getAxiosClient().get<StatsPatientOriginByMunicipalityDTO[]>(
    '/stats/patients/origin/municipalities',
    { params: normalizedParams },
  )

  return response.data
}

export const fetchPatientCareTypeBreakdown = async (
  params: GetPatientStatsQuery,
): Promise<StatsPatientCareTypeBreakdownDTO[]> => {
  const normalizedParams = normalizeStatsDateRangeQuery(
    getBasePatientStatsQuery(params),
  )

  const response = await getAxiosClient().get<StatsPatientCareTypeBreakdownDTO[]>(
    '/stats/patients/care-type/breakdown',
    { params: normalizedParams },
  )

  return response.data
}

export const fetchPatientGenderBreakdown = async (
  params: GetPatientStatsQuery,
): Promise<StatsPatientGenderBreakdownDTO[]> => {
  const queryParams = getPatientGenderStatsQuery(params)

  const response = await getAxiosClient().get<StatsPatientGenderBreakdownDTO[]>(
    '/stats/patients/gender/breakdown',
    { params: queryParams },
  )

  return response.data
}

export const fetchPatientAgeSummary = async (
  params: GetPatientStatsQuery,
): Promise<StatsPatientAgeSummaryDTO> => {
  const queryParams = getPatientAgeSummaryQuery(params)

  const response = await getAxiosClient().get<StatsPatientAgeSummaryDTO>(
    '/stats/patients/age/summary',
    { params: queryParams },
  )

  return response.data
}
